// src/App.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom'; // Import Router components
import { throttle } from 'lodash'; // Keep if using throttle
import './App.css';

// Import Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
// Import Pages
import HomePage from './pages/HomePage';
import LegalPage from './pages/LegalPage';
// Assuming you created these page components earlier

// Define section IDs used in Navbar and for scrolling on HomePage
const SECTION_IDS = ['why-us', 'details', 'services', 'portfolio', 'contact'];

function App() {
  const [activeSection, setActiveSection] = useState('');
  const navbarHeightRef = useRef(70); // Store navbar height

  // Scroll handler for active nav link detection on HomePage
  const handleScroll = useCallback(() => {
    // Only run this logic if we are on the home page ('/' relative to base)
    // location.pathname from react-router already excludes basename
    if (window.location.pathname !== '/') {
        setActiveSection(''); // Clear active section on other pages
        return;
    }

    let currentSection = '';
    const scrollPosition = window.scrollY + navbarHeightRef.current + 100; // Offset below navbar

    for (const sectionId of SECTION_IDS) {
      const sectionElement = document.getElementById(sectionId);
      if (sectionElement) {
        const sectionTop = sectionElement.offsetTop;
        if (scrollPosition >= sectionTop) {
          currentSection = sectionId;
        } else {
          break;
        }
      }
    }

    const firstSectionElement = document.getElementById(SECTION_IDS[0]);
    if (firstSectionElement && window.scrollY < firstSectionElement.offsetTop - navbarHeightRef.current - 50) {
       currentSection = '';
    }

    setActiveSection(prevSection => {
        if (prevSection !== currentSection) {
            return currentSection;
        }
        return prevSection;
    });

  }, []);

  const throttledScrollHandler = useMemo(() => throttle(handleScroll, 150), [handleScroll]);

  // Effect for scroll listener (Active Nav Link)
  useEffect(() => {
    window.addEventListener('scroll', throttledScrollHandler);
    throttledScrollHandler(); // Initial check

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      throttledScrollHandler.cancel(); // Cancel throttled calls on unmount
    };
  }, [throttledScrollHandler]);

  // Effect for Intersection Observer (Tape Animation)
  useEffect(() => {
    const animatedTitles = document.querySelectorAll('.section-title-animated');
    if (!animatedTitles.length) return;

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -100px 0px',
      threshold: 0.1
    };

    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    };

    const intersectionObserver = new IntersectionObserver(observerCallback, observerOptions);
    animatedTitles.forEach(title => intersectionObserver.observe(title));

    return () => {
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
    };
  }, []); // Run once on mount

  return (
    // REMOVED basename prop - relies on build process via homepage setting
    <BrowserRouter>
      <div className="App">
        <Navbar activeSection={activeSection} />
        <main>
          <Routes>
             {/* Paths are relative to homepage/base path */}
            <Route path="/" element={<HomePage />} />
            <Route path="/rechtliches" element={<LegalPage />} />
            {/* Optional: Add a 404 Not Found route */}
            {/* <Route path="*" element={<NotFoundPage />} /> */}
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;